import moment from "moment";

import { realTimeNotificationClient } from "@/Common/Api/RealTimeNotification/SignalRClient";
import { AuthenticatorCodeError, Login as MyleModelsResponseAuthenticationLogin, SessionCheck } from "@/Common/Models/Authentication/Login";
import { Resume } from "@/Common/Models/Authentication/Resume";
import { ClientConfiguration } from "@/Common/Models/ClientConfiguration";
import { ILoginErrorMessage } from "@/Common/Models/UI/Login/ILoginModel";
import { WalkmeMetadataModel } from "@/Common/Models/WalkMeMetaDataModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { BaseServerOperationHandler } from "@/Common/Stores/Handlers/Server/BaseServerOperationHandler";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { LoginWorkflow } from "@/Common/Workflow/LoginWorkflow/LoginWorkflow";
import { LoginWorkflowStepEnum } from "@/Common/Workflow/LoginWorkflow/LoginWorkflowStepEnum";
import { SessionStore } from "@/Globals/Stores/SessionStore";

class SessionResponseHandler extends BaseServerOperationHandler {
    constructor(action: BaseAction) {
        super(action);

        this.RegisterOperation(ServerOperations.Server.SessionCheck, this.checkSession);
        this.RegisterOperation(ServerOperations.Server.Myle_Logoff, this.logoffBackToHome, SubServerOperations.Login.LogoutToHomeScreen);
        this.RegisterOperation(ServerOperations.Server.Myle_Logoff, this.logoffSessionCheck, SubServerOperations.Login.LogoutWithSessionCheck);
        this.RegisterOperation(ServerOperations.Server.ResumeSession, this.resumeSession);
        this.RegisterOperation(ServerOperations.Server.Myle_PwdRecovery_Password_Reset_Email, this.handleSendPasswordResetResponse);
        this.RegisterOperation(ServerOperations.Server.Myle_Login, this.login);

        // login workflow steps
        this.RegisterOperation(ServerOperations.Server.Myle_Verify_Email, this.handleVerifyEmailResponse);
        this.RegisterOperation(ServerOperations.Server.Myle_LoadCache, this.handleCacheLoadedResponse);
        this.RegisterOperation(ServerOperations.Server.MedfarDictionnary_SearchSpecialisation, this.handleTitleSpecialtyCacheLoadedResponse);
        this.RegisterOperation(ServerOperations.Server.Myle_AcceptAgreement, this.handleAcceptedAgreementResponse);
        this.RegisterOperation(ServerOperations.Server.UpdateEmployeeType, this.handleUserTypeAssignmentResponse);
        this.RegisterOperation(ServerOperations.Server.Myle_UpdatePromptedForRecoveryEmailAttribute, this.updatePromptedForRecoveryEmailAttributeResponse);
        this.RegisterOperation(ServerOperations.Server.Myle_MultifactorAuthenticationModule_Login_SkipSetup, this.UpdatePromptedDateForLoginMultifactorAuthenticatorResponse);
        this.RegisterOperation(ServerOperations.Server.Myle_AuthenticationModule_GetAccessInfo, this.login);
        this.RegisterOperation(ServerOperations.Server.Myle_MultifactorAuthenticationModule_CurrentUser_Activate, this.ActivateLoginMultifactorAuthenticationResponse);
    }

    private login(response: ServerEvent, store: SessionStore): void {
        let rawResponse = response.Data;
        if (!rawResponse.ClientConfiguration) {
            rawResponse.ClientConfiguration = new ClientConfiguration();
        }
        const loginResponse: MyleModelsResponseAuthenticationLogin = rawResponse;
        store.clientLoginModel = store.clientLoginModel
            .set("LoggedIn", loginResponse.LoggedIn)
            .set("ValidatingCredentials", false)
            .set("SignInRequired", false);

        store.clientConfiguration = loginResponse.ClientConfiguration == null ? new ClientConfiguration() : loginResponse.ClientConfiguration;

        if (loginResponse.LoggedIn) {
            this.onSuccessfulLogin(loginResponse, store);
        }
        else if (loginResponse.AuthenticatorCodeError === AuthenticatorCodeError.Missing && !store.clientLoginModel.get("ShowAuthenticatorCodeForm")) {
            store.clientLoginModel = store.clientLoginModel
                .set("ShowAuthenticatorCodeForm", true)
                .set("SendAuthenticatorCodeByEmailToken", loginResponse.SendAuthenticatorCodeByEmailToken)
                .set("SelectedAuthenticatorCodeDeliveryMethod", loginResponse.AuthenticatorCodeDeliveryMethod);
        }
        else if (loginResponse.AuthenticatorCodeError === AuthenticatorCodeError.Invalid) {
            store.clientLoginModel = store.clientLoginModel
                .set("ErrorMessage", this.getLoginErrorMessage())
                .set("AuthenticatorCode", "");
        }
        else {
            const e: ILoginErrorMessage = this.getLoginErrorMessage();
            store.clientLoginModel = store.clientLoginModel.set("ErrorMessage", e);
            window.setTimeout(() => $("#password").focus(), 200);
        }
    }

    private onSuccessfulLogin(loginResponse: MyleModelsResponseAuthenticationLogin, store: SessionStore): void {
        this.setSession(loginResponse, store);
        WalkmeMetadataModel.Initialize();
        realTimeNotificationClient.connect();
        store.loginWorkflow = new LoginWorkflow();
    }

    public setSession(loginResponse: MyleModelsResponseAuthenticationLogin, store: SessionStore): void {
        apiSettings.setSession(loginResponse.SecurityTokenHash, loginResponse.SessionId, "login");
        cacheStore.SessionLengthMinutes = loginResponse.SessionDurationMinutes;

        store.lastValidUserName = store.clientLoginModel.get("UserName");
        store.userId = loginResponse.UserId;
        store.userWalkmeActivationDate = LuxonDateTimeParser.parse(loginResponse.WalkmeActivationDate)?.toJSDate();
        store.individualContactId = loginResponse.IndividualContactId;
        store.userPermissions = loginResponse.Permissions;
        store.myleEmployeeType = loginResponse.EmployeeType;
        store.myleEmployeeTitle = loginResponse.EmployeeTitle;
        store.acceptedAgreement = loginResponse.RequiresAcceptAgreement == false;
        store.hasEmail = loginResponse.HasEmails;
        store.unverifiedEmail = loginResponse.UnverifiedEmail;
        store.lastRecoveryEmailPromptDate = loginResponse.LastRecoveryEmailPromptDate;
        store.lastVerifiedEmail = loginResponse.LastVerifiedEmail;
        store.MultifactorAuthenticationSetupPromptDates = loginResponse.MultifactorAuthenticationSetupPromptDates;
        store.isRecoveryEmailRequired = loginResponse.IsRecoveryEmailRequired;
        store.IsMultifactorAuthenticationRequired = loginResponse.IsMultifactorAuthenticationRequired;
        store.isRequiredEmailFlowEnded = loginResponse.HasEmails && !loginResponse.UnverifiedEmail;
        store.signOnPrintAccess = loginResponse.SignOnPrintAccess;
        store.hasPinSet = loginResponse.HasPinSet;
        store.useMetricSystemAsDefault = loginResponse.UseMetricSystemAsDefault;
        store.isTempPassword = loginResponse.IsTempPassWord;
        store.clientLoginModel = store.clientLoginModel
            .set("IsNewAdminUser", loginResponse.IsNewAdminUser)
            .set("IsCacheLoaded", false);
        store.userCreationDate = LuxonDateTimeParser.parse(loginResponse.UserCreationDate)?.toJSDate();
        store.goLiveDate = LuxonDateTimeParser.parse(loginResponse.GoLiveDate)?.toJSDate();
        store.AuthenticatorCodeDeliveryMethod = loginResponse.AuthenticatorCodeDeliveryMethod;
        store.IsMultifactorAuthenticationEnabled = loginResponse.IsMultifactorAuthenticationEnabled;

        store.goLiveDate = loginResponse.GoLiveDate;
        // temporarily hide fax access in MYLE until it is done
        store.faxAccess = loginResponse.FaxAccess;

        store.customPasswordRuleMessage = loginResponse.CustomPasswordRuleMessage;
        store.customAdminPasswordRuleMessage = loginResponse.CustomAdminPasswordRuleMessage;
        store.customPasswordValidationRegex = loginResponse.PasswordValidationRegex;

        // set user language code and date string locale
        store.setLanguageCode(loginResponse.UserLanguageCode);
        moment.locale(store.userLanguageCode, { week: { dow: 0, doy: 6 } });

        // clear form || leave username
        store.clientLoginModel = store.clientLoginModel
            .set("Password", "")
            .set("AuthenticatorCode", "")
            .set("LoggedIn", true)
            .set("SignInRequired", false)
            .set("ShowAuthenticatorCodeForm", false);

        // start udke session timer
        store.logSession("Starting session inactivity check. First token expiring @" + moment.utc(loginResponse.SessionExpiration).format("HH:mm:ss") + "UTC");
        store.startSessionInactivityCheck(moment.utc(loginResponse.SessionExpiration).toDate());
    }

    private getLoginErrorMessage(): ILoginErrorMessage {
        const messageKey = sessionStore.clientLoginModel.get("ShowAuthenticatorCodeForm")
            ? "LoginFailedWithAuthenticatorCodeInvalidLabel"
            : "LoginFailed";

        return { UseLocalResource: true, Message: messageKey };
    }

    private logoffBackToHome(response: ServerEvent, store: SessionStore): void {
        store.clientLoginModel = store.clientLoginModel.set("LoggedIn", false);
        store.toggleNavigationPrompt(false);
        document.location.href = rootPath;
    }

    private logoffSessionCheck(response: ServerEvent, store: SessionStore): void {
        // this will call a session check - if it returns with session timeout, client class will redirect to myle session inactivated page
        store.action.checkSession();
    }

    private resumeSession(response: ServerEvent, store: SessionStore): void {
        let loginResponse: Resume = response.Data;
        store.clientLoginModel = store.clientLoginModel
            .set("ValidatingCredentials", false)
            .set("LoggedIn", loginResponse.LoggedIn)
            .set("SignInRequired", false);

        if (loginResponse.LoggedIn) {
            apiSettings.setSession(loginResponse.SecurityTokenHash, loginResponse.SessionId, "session resume");

            store.clientLoginModel = store.clientLoginModel
                .set("Password", "")
                .set("AuthenticatorCode", "")
                .set("ShowAuthenticatorCodeForm", false);

            // start idle session timer
            sessionStore.startSessionInactivityCheck(loginResponse.SessionExpirationTime);

            RouterHistory.go(-1);

            store.toggleNavigationPrompt(true);
            realTimeNotificationClient.connect();

            store.dispatch(LocalOperations.Documents.ResetCache);
            store.dispatch(LocalOperations.Portfolio.ResetCache);
        }
        else if (loginResponse.AuthenticatorCodeError === AuthenticatorCodeError.Missing && !store.clientLoginModel.get("ShowAuthenticatorCodeForm")) {
            store.clientLoginModel = store.clientLoginModel.set("ShowAuthenticatorCodeForm", true);
        }
        else if (loginResponse.AuthenticatorCodeError === AuthenticatorCodeError.Invalid) {
            store.clientLoginModel = store.clientLoginModel
                .set("ErrorMessage", this.getLoginErrorMessage())
                .set("AuthenticatorCode", "");
        }
        else {
            store.clientLoginModel = store.clientLoginModel.set("ValidatingCredentials", false);
            const e: ILoginErrorMessage = this.getLoginErrorMessage();
            store.clientLoginModel = store.clientLoginModel
                .set("ErrorMessage", e)
                .set("Password", "");

            window.setTimeout(() => $("#password").focus(), 200);
        }
    }

    private checkSession(response: ServerEvent, store: SessionStore): void {
        let sessionCheck: SessionCheck = response.Data;

        if (!moment.utc(store.tokenExpiration).isSame(moment.utc(sessionCheck.SessionExpirationTime))) {
            store.logSession("Check session :  session slide executed. New expiration @" + moment.utc(sessionCheck.SessionExpirationTime).format("HH:mm:ss") + "UTC");
            store.startSessionInactivityCheck(sessionCheck.SessionExpirationTime);
        }
        else
            store.logSession("Check session : no action taken. Token expiration @" + moment.utc(sessionCheck.SessionExpirationTime).format("HH:mm:ss") + "UTC");
    }

    private handleSendPasswordResetResponse(response: ServerEvent, store: SessionStore): void {
        store.clientLoginModel = store.clientLoginModel.set("RequestResetPasswordInProgress", false);

        if (response.Errors != null && response.Errors.length > 0) {
            let e: ILoginErrorMessage = { UseLocalResource: true, Message: response.Errors.firstOrDefault().Code };
            store.clientLoginModel = store.clientLoginModel.set("RequestResetPasswordErrorMessage", e);

            return;
        }

        store.clientLoginModel = store.clientLoginModel.set("OpenRequestResetPasswordControl", false);
        store.clientLoginModel = store.clientLoginModel.set("RequestResetPasswordErrorMessage", null);
    }

    private handleCacheLoadedResponse(response: ServerEvent, store: SessionStore): void {
        // go to next step if completed
        store.completeWorkflowStep(LoginWorkflowStepEnum.LoadCache);
    }

    private handleTitleSpecialtyCacheLoadedResponse(response: ServerEvent, store: SessionStore): void {
        // go to next step if completed
        store.completeWorkflowStep(LoginWorkflowStepEnum.LoadTitlesAndSpecialtiesCache);
    }

    private handleVerifyEmailResponse(response: ServerEvent, store: SessionStore): void {
        if (response.Errors != null && response.Errors.length > 0) {
            let e: ILoginErrorMessage = { UseLocalResource: true, Message: response.Errors.firstOrDefault().Code };
            store.clientLoginModel = store.clientLoginModel.set("VerifyEmailErrorMessage", e);

            return;
        }

        store.isRequiredEmailFlowEnded = true;
        // go to next step if completed
        store.completeWorkflowStep(LoginWorkflowStepEnum.RequiredRecoveryEmailFlow);
    }

    private handleAcceptedAgreementResponse(response: ServerEvent, store: SessionStore): void {
        let pass: boolean = response.Data.Pass;
        store.clientLoginModel = store.clientLoginModel.set("AcceptingAgreementInProgress", false);
        if (pass) {
            store.acceptedAgreement = true;

            // go to next step if completed
            store.completeWorkflowStep(LoginWorkflowStepEnum.AcceptAgreement);
        }
    }

    private handleUserTypeAssignmentResponse(response: ServerEvent): void {
        // update the user in session store
        if (response.Data["IsSuccess"]) {
            let currentEmployee = sessionStore.getCurrentEmployee();
            currentEmployee.Model.Licences[0].IssuingLicenseOrganizationRuleId = response.Data["LicenseOrganizationRuleId"];
            currentEmployee.Model.LicenseOrganizationSpecializationId = response.Data["LicenseOrganizationSpecializationId"];
            sessionStore.rebindCurrentEmployee(currentEmployee);

            // force the user to log back in
            // The backend session is created only during the login step and can't change after it's created, but the
            // employee title info is needed for certain permissions in the request handlers. Once the user selects
            // their licence organization and title and logs back in, a new session will be created and the employee
            // title will be added to the session details.
            sessionStore.action.logout(false);
        }
        else
            sessionStore.showPopup(response.Errors);
    }

    private updatePromptedForRecoveryEmailAttributeResponse(response: ServerEvent): void {
        // go to the next step of recovery email flow
        if (response.Data.IsSuccess)
            sessionStore.cancelWorkflowStep(LoginWorkflowStepEnum.RequiredRecoveryEmailFlow);
    }

    private UpdatePromptedDateForLoginMultifactorAuthenticatorResponse(response: ServerEvent): void {
        // skip MFA setup, go to login flow
        if (response.Data.IsSuccess)
            sessionStore.cancelWorkflowStep(LoginWorkflowStepEnum.RequiredMandatoryMultiFactorAuthenticationSetUpFlow);
    }

    private ActivateLoginMultifactorAuthenticationResponse(response: ServerEvent, store: SessionStore): void {
        // complete MFA setup, go to login flow
        if (response.Errors == null || response.Errors.length == 0) {
            store.IsMultifactorAuthenticationEnabled = true;
            sessionStore.completeWorkflowStep(LoginWorkflowStepEnum.RequiredMandatoryMultiFactorAuthenticationSetUpFlow);
        }
    }
}

export { SessionResponseHandler };
